import { t } from "i18next";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Club } from "../../../../interfaces/club";
import { Athlete, AthleteTeamSeason } from "../../../../interfaces/athlete";
import { getAthlete, createBulkAthletePerformance, getAthlete as refreshAthletes } from "../../../../services/athlete";
import { AdminAthleteNew } from "./athlete-new";
import { AdminAthleteEdit } from "./athlete-edit";
import { AdminAthleteDelete } from "./athlete-delete";
import moment from 'moment';
import { getClubOptions } from "../../../../services/club";
import { getTeamOptions } from "../../../../services/team";
import avatar from "../../../../assets/images/avatar.svg";
import { AdminAthleteTeamSeason } from "./athlete-team-season";
import { Link } from "react-router-dom";
import { AdminAthleteRanking } from "./athlete-ranking";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { CoachAthletePerformance } from "../../coach/athletes/athlete-performance";
import { useKpiSettings } from "../../../../contexts/KpiSettingsContext";
import { timeToSeconds, formatKpiValue } from "../../../../util/time-format";

export function AdminAthleteList() {
    const { getKpiLabelWithUnit, getActiveKpis, kpiSettingsLoaded } = useKpiSettings();
    const [athletes, setAthletes] = useState<Athlete[]>([]);
    const [clubOptions, setClubOptions] = useState<any[]>([]);
    const [teamOptions, setTeamOptions] = useState<any[]>([]);
    const [showAddNew, setShowAddNew] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showAthleteTeamSeason, setShowAthleteTeamSeason] = useState(false);
    const [showAthleteRanking, setShowAthleteRanking] = useState(false);
    const [showPerformance, setShowPerformance] = useState(false);
    const [selectedId, setSelectedId] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [quickAddMode, setQuickAddMode] = useState(false);
    const [performanceDate, setPerformanceDate] = useState(moment().format('YYYY-MM-DD'));
    const [performanceInputs, setPerformanceInputs] = useState<{[athleteId: number]: Record<string, string | number>}>({});
    const [searchParams, setSearchParams] = useState({
        search: '',
        club: '',
        team: '',
        sortbyfield: 'updated_at',
        sortbytype: 'DESC'
    });

    useEffect(() => {
        Promise.all([
            getClubOptions(),
            getAthlete('', '', '', '', 'updated_at', 'DESC', 1, 12)
        ]).then(response => {
            setClubOptions(response[0].data);
            const athletesData = response[1].data;
            setAthletes(athletesData);
            setHasMore(athletesData.length === 12);
            setLoaded(true);
        });
    }, []);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const club = event.target.club.value;
        let team = '';
        if (event.target.team !== undefined) {
            team = event.target.team.value;
        }
        const sortValue = event.target.sortby.value;
        const lastUnderscoreIndex = sortValue.lastIndexOf('_');
        const sortbyfield = sortValue.substring(0, lastUnderscoreIndex);
        const sortbytype = sortValue.substring(lastUnderscoreIndex + 1);

        setSearchParams({ search: s, club, team, sortbyfield, sortbytype });
        setCurrentPage(1);

        getAthlete(s, club, team, '', sortbyfield, sortbytype, 1, 12)
            .then(response => {
                const athletesData = response.data;
                setAthletes(athletesData);
                setHasMore(athletesData.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleLoadMore = (event: any) => {
        event.preventDefault();
        const nextPage = currentPage + 1;

        getAthlete(
            searchParams.search,
            searchParams.club,
            searchParams.team,
            '',
            searchParams.sortbyfield,
            searchParams.sortbytype,
            nextPage,
            12
        )
            .then(response => {
                const newAthletes = response.data;
                setAthletes([...athletes, ...newAthletes]);
                setCurrentPage(nextPage);
                setHasMore(newAthletes.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAddNew(true)
    }

    const handleAddNewOnSave = (s: Athlete) => {
        const newAthletes = [
            s,
            ...athletes
        ];
        setAthletes(newAthletes);
        setShowAddNew(false);
    }

    const handleAddNewOnClose = () => {
        setShowAddNew(false);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDeleteOnDelete = (id: number) => {
        setSelectedId(0);
        const newAthletes = athletes.filter((m: Athlete) => m.id != id);
        setAthletes(newAthletes);
        setShowDelete(false);
    }

    const handleDeleteOnClose = () => {
        setSelectedId(0);
        setShowDelete(false);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        const newAthletes = athletes.map((m: Athlete) => {
            if (m.id == changedAthlete.id) {
                return changedAthlete;
            } else {
                return m;
            }
        });
        setAthletes(newAthletes);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    const handleAthleteTeamSeason = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowAthleteTeamSeason(true);
    }

    const handleAthleteTeamSeasonOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        /*const newAthletes = athletes.map((m: Athlete) => {
            if (m.id == changedAthlete.id) {
                return changedAthlete;
            } else {
                return m;
            }
        });
        setAthletes(newAthletes);*/
        setShowAthleteTeamSeason(false);
    }

    const handleAthleteTeamSeasonOnClose = () => {
        setSelectedId(0);
        setShowAthleteTeamSeason(false);
    }

    const handleAthleteRanking = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowAthleteRanking(true);
    }

    const handleAthleteRankingOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        /*const newAthletes = athletes.map((m: Athlete) => {
            if (m.id == changedAthlete.id) {
                return changedAthlete;
            } else {
                return m;
            }
        });
        setAthletes(newAthletes);*/
        setShowAthleteRanking(false);
    }

    const handleAthleteRankingOnClose = () => {
        setSelectedId(0);
        setShowAthleteRanking(false);
    }

    const handlePerformance = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowPerformance(true);
    }

    const handlePerformanceOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        setShowPerformance(false);
    }

    const handlePerformanceOnClose = () => {
        setSelectedId(0);
        setShowPerformance(false);
    }

    const handleClubChange = (event: any) => {
        if (event.target.value.length > 0) {
            getTeamOptions(event?.target.value)
                .then((response: any) => {
                    setTeamOptions(response.data.map((t: any) => {
                        return {
                            value: t.id,
                            label: t.name
                        }
                    }))
                })
                .catch((error: any) => {
                    console.log(error);
                });
        } else {
            setTeamOptions([]);
        }
    }

    const handleViewModeToggle = () => {
        const newMode = viewMode === 'cards' ? 'table' : 'cards';
        setViewMode(newMode);

        if (newMode === 'table') {
            getAthlete(
                searchParams.search,
                searchParams.club,
                searchParams.team,
                '',
                searchParams.sortbyfield,
                searchParams.sortbytype,
                1,
                999999
            )
                .then(response => {
                    setAthletes(response.data);
                    setHasMore(false);
                })
                .catch(error => {
                    console.log(error);
                });
        } else {
            setCurrentPage(1);
            getAthlete(
                searchParams.search,
                searchParams.club,
                searchParams.team,
                '',
                searchParams.sortbyfield,
                searchParams.sortbytype,
                1,
                12
            )
                .then(response => {
                    const athletesData = response.data;
                    setAthletes(athletesData);
                    setHasMore(athletesData.length === 12);
                })
                .catch(error => {
                    console.log(error);
                });
        }
    }

    const calculateAveragePerformance = (performances: any[], kpiName: string, dataType: 'decimal' | 'time' = 'decimal') => {
        if (!performances || performances.length === 0) return '-';

        const validValues = performances
            .map(p => {
                if (p.kpis && p.kpis[kpiName] !== undefined) {
                    return parseFloat(p.kpis[kpiName].toString()) || 0;
                } else if (p[kpiName] !== undefined) {
                    return parseFloat(p[kpiName].toString()) || 0;
                }
                return 0;
            })
            .filter(value => value > 0);

        if (validValues.length === 0) return '-';

        const sum = validValues.reduce((acc, value) => acc + value, 0);
        const avg = sum / validValues.length;

        return formatKpiValue(avg, dataType === 'time' ? 'time' : 'decimal');
    }

    const handleToggleQuickAdd = () => {
        setQuickAddMode(!quickAddMode);
        if (!quickAddMode) {
            setPerformanceDate(moment().format('YYYY-MM-DD'));
            setPerformanceInputs({});
        }
    }

    const handlePerformanceInputChange = (athleteId: number, kpiName: string, value: string) => {
        setPerformanceInputs(prev => {
            const athleteKpis = { ...prev[athleteId] };

            if (value === '' || value === null || value === undefined) {
                delete athleteKpis[kpiName];
            } else {
                athleteKpis[kpiName] = value;
            }

            return {
                ...prev,
                [athleteId]: athleteKpis
            };
        });
    }

    const handleSavePerformances = async () => {
        const activeKpis = getActiveKpis();
        const kpiFormats = Object.fromEntries(
            activeKpis.map(kpi => [kpi.kpi_name, kpi.data_type])
        );

        const athletesPerformance = Object.entries(performanceInputs)
            .filter(([_, kpis]) => {
                return Object.keys(kpis).length > 0;
            })
            .map(([athleteId, kpis]) => {
                const convertedKpis: Record<string, number> = {};
                Object.entries(kpis).forEach(([kpiName, value]) => {
                    if (kpiFormats[kpiName] === 'time' && typeof value === 'string') {
                        convertedKpis[kpiName] = timeToSeconds(value);
                    } else {
                        convertedKpis[kpiName] = parseFloat(value as string);
                    }
                });

                return {
                    athlete_id: parseInt(athleteId),
                    kpis: convertedKpis
                };
            });

        if (athletesPerformance.length === 0) {
            alert(t("No performance data to save"));
            return;
        }

        const body = {
            performance_timestamp: performanceDate + ' 00:00:00',
            athletes_performance: athletesPerformance
        };

        try {
            const response = await createBulkAthletePerformance(body);
            if (response.data.status == 1) {
                alert(t("Performance data saved successfully"));
                setQuickAddMode(false);
                setPerformanceInputs({});

                refreshAthletes(
                    searchParams.search,
                    searchParams.club,
                    searchParams.team,
                    '',
                    searchParams.sortbyfield,
                    searchParams.sortbytype,
                    1,
                    999999
                ).then(response => {
                    setAthletes(response.data);
                }).catch(error => {
                    console.log(error);
                });
            } else {
                alert(t("Error adding performance data. Please try again."));
            }
        } catch (error) {
            console.log(error);
            alert(t("Error adding performance data. Please try again."));
        }
    }

    const handleCancelPerformances = () => {
        setPerformanceInputs({});
        setQuickAddMode(false);
        setPerformanceDate(moment().format('YYYY-MM-DD'));
    }

    return (
        <div className="athletes-list-container">
            <h1>
                { t('athletes')}
            </h1>
            { !loaded && <LoadingSpinner /> }
            { loaded &&
                <div className="filters-container">
                    <Form onSubmit={ handleSearchSubmit }>
                        <Form.Group className="field-search" controlId="search">
                            <Form.Control type="text" placeholder={ t("Write to search") } name="search" />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="club_id">
                            <Form.Select aria-label={ t("club") } name="club" onChange={ handleClubChange }>
                                <option value="">{ t("select_club") }</option>
                                { clubOptions.map((c: Club) => {
                                    return <option key={ 'club-' + c.id } value={ c.id }>{ c.name }</option>;
                                })}
                            </Form.Select>
                        </Form.Group>
                        { teamOptions.length > 0 &&
                            <Form.Group className="mb-3" controlId="team">
                                <Form.Select aria-label={ t("team") } name="team">
                                    <option value="">{ t("select_team") }</option>
                                    { teamOptions.map((t: any) => {
                                        return <option key={ 'team-' + t.value } value={ t.value }>{ t.label }</option>;
                                    })}
                                </Form.Select>
                            </Form.Group>
                        }
                        <Form.Group className="mb-3" controlId="sortby">
                            <Form.Select aria-label={ t("Sort by") } name="sortby" defaultValue={ 'updated_at_DESC' }>
                                <option value="updated_at_DESC">{ t("Updated") } ({ t("Newest") })</option>
                                <option value="updated_at_ASC">{ t("Updated") } ({ t("Oldest") })</option>
                                <option value="name_ASC">{ t("Name") } (A-Z)</option>
                                <option value="name_DESC">{ t("Name") } (Z-A)</option>
                            </Form.Select>
                        </Form.Group>
                        <div className="actions">
                            <Button variant="primary" type="submit">
                                { t("search") }
                            </Button>
                            <Button variant="secondary" onClick={ handleViewModeToggle } className="ms-2">
                                <i className={ viewMode === 'cards' ? 'bi bi-table' : 'bi bi-grid-3x3-gap' }></i>
                                { viewMode === 'cards' ? ' ' + t("Table view") : ' ' + t("Card view") }
                            </Button>
                        </div>
                    </Form>
                </div>
            }
            { loaded && viewMode === 'cards' && (
                <div className="grid-container">
                    <Row>
                        { athletes.map((a: Athlete) => {
                            return <Col key={ 'athlete-' + a.id } xs={12} md={6} lg={4}>
                                <div className="athlete-card">
                                    <div className="heading">
                                        <div className="avatar">
                                            { a.avatar.length > 0 &&
                                                <img src={ a.avatar[0].url } alt={ a.name } />
                                            }
                                            { a.avatar.length == 0 &&
                                                <img src={ avatar } alt={ a.name } />
                                            }
                                        </div>
                                        <div className="meta">
                                            <div className="name">
                                                <Link to={ '/athletes/' + a.id + '/view' } title={ a.name } target="_blank">
                                                    { a.name }
                                                </Link>
                                            </div>
                                            { a.birthdate &&
                                                <div className="years">
                                                    { moment(new Date()).diff(a.birthdate, 'years') + ' ' + t("years").toLowerCase() }
                                                </div>
                                            }
                                            <div className="team">
                                                {
                                                    a.registered_in.map((ats: AthleteTeamSeason) => {
                                                        return ats.club_name + '/' + ats.team_name;
                                                    }).join(', ')
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div className="actions">
                                        <a href="#" className="icon-link edit" title={ t("Edit") } onClick={ handleEdit } data-id={ a.id }>
                                            <i className="bi bi-pencil-square"></i>
                                        </a>
                                        <a href="#" className="icon-link team-season" title={ t("athlete_team_seasons") } onClick={ handleAthleteTeamSeason } data-id={ a.id }>
                                            <i className="bi bi-calendar-check"></i>
                                        </a>
                                        <a href="#" className="icon-link ranking" title={ t("Ranking") } onClick={ handleAthleteRanking } data-id={ a.id }>
                                            <i className="bi bi-shield-check"></i>
                                        </a>
                                        <a href="#" className="icon-link performance" title={ t("Performance") } onClick={ handlePerformance } data-id={ a.id }>
                                            <i className="bi bi-bar-chart-steps"></i>
                                        </a>
                                        {<a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ a.id }>
                                            <i className="bi bi-trash"></i>
                                        </a>}
                                    </div>
                                </div>
                            </Col>
                        })}
                    </Row>
                    { hasMore &&
                        <div className="load-more-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                            <Button variant="primary" onClick={ handleLoadMore }>
                                { t("Load more") }
                            </Button>
                        </div>
                    }
                </div>
            )}
            { loaded && viewMode === 'table' && (
                <div className="table-container">
                    <div className="table-actions mb-3 d-flex align-items-center gap-3">
                        {!quickAddMode ? (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleToggleQuickAdd}
                            >
                                <i className="bi bi-plus-circle"></i>
                                {' ' + t("Add performance data")}
                            </Button>
                        ) : (
                            <>
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 me-2">{t("Date")}:</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={performanceDate}
                                        onChange={(e) => setPerformanceDate(e.target.value)}
                                        required
                                        style={{ width: '200px' }}
                                    />
                                </div>
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleSavePerformances}
                                >
                                    <i className="bi bi-save"></i>
                                    {' ' + t("Save data")}
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleCancelPerformances}
                                >
                                    <i className="bi bi-x-circle"></i>
                                    {' ' + t("Cancel")}
                                </Button>
                            </>
                        )}
                    </div>
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>{ t("Avatar") }</th>
                                <th>{ t("Name") }</th>
                                {getActiveKpis().map(kpi => (
                                    <th key={kpi.kpi_name} className="text-center" style={{ minWidth: '120px' }}>
                                        { getKpiLabelWithUnit(kpi.kpi_name) }
                                    </th>
                                ))}
                                <th className="text-center" style={{ width: '150px' }}>{ t("actions") }</th>
                            </tr>
                        </thead>
                        <tbody>
                            { athletes.map((a: Athlete) => {
                                const clubTeamInfo = a.registered_in?.find(r => r.current)
                                    ? `${a.registered_in.find(r => r.current)?.club_name} / ${a.registered_in.find(r => r.current)?.team_name}`
                                    : (a.registered_in && a.registered_in.length > 0 ? `${a.registered_in[0].club_name} / ${a.registered_in[0].team_name}` : '-');

                                return <tr key={ 'athlete-row-' + a.id }>
                                    <td>
                                        <div className="avatar-small">
                                            { a.avatar.length > 0 &&
                                                <img src={ a.avatar[0].url } alt={ a.name } />
                                            }
                                            { a.avatar.length == 0 &&
                                                <img src={ avatar } alt={ a.name } />
                                            }
                                        </div>
                                    </td>
                                    <td>
                                        <div className="athlete-name-cell">
                                            <Link to={ '/athletes/' + a.id + '/view' } title={ a.name } className="athlete-name" target="_blank">
                                                { a.name }
                                            </Link>
                                            <div className="club-team-info">
                                                { clubTeamInfo }
                                            </div>
                                        </div>
                                    </td>
                                    {getActiveKpis().map(kpi => (
                                        <td key={kpi.kpi_name} className="text-center">
                                            {quickAddMode ? (
                                                kpi.data_type === 'time' ? (
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="45.494 o 01:45.494"
                                                        value={performanceInputs[a.id]?.[kpi.kpi_name] ?? ''}
                                                        onChange={(e) => handlePerformanceInputChange(a.id, kpi.kpi_name, e.target.value)}
                                                        style={{ width: '150px', margin: '0 auto', fontSize: '0.85rem' }}
                                                        title="Formatos aceptados: ss.ms, mm:ss.ms o hh:mm:ss.ms"
                                                    />
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        placeholder={kpi.unit || '0.00'}
                                                        value={performanceInputs[a.id]?.[kpi.kpi_name] ?? ''}
                                                        onChange={(e) => handlePerformanceInputChange(a.id, kpi.kpi_name, e.target.value)}
                                                        style={{ width: '100px', margin: '0 auto' }}
                                                    />
                                                )
                                            ) : (
                                                calculateAveragePerformance(a.performance, kpi.kpi_name, kpi.data_type)
                                            )}
                                        </td>
                                    ))}
                                    <td className="text-center">
                                        {!quickAddMode && (
                                            <>
                                                <a href="#" className="icon-link edit me-2" title={ t("Edit") } onClick={ handleEdit } data-id={ a.id }>
                                                    <i className="bi bi-pencil-square"></i>
                                                </a>
                                                <a href="#" className="icon-link team-season me-2" title={ t("athlete_team_seasons") } onClick={ handleAthleteTeamSeason } data-id={ a.id }>
                                                    <i className="bi bi-calendar-check"></i>
                                                </a>
                                                <a href="#" className="icon-link ranking me-2" title={ t("Ranking") } onClick={ handleAthleteRanking } data-id={ a.id }>
                                                    <i className="bi bi-shield-check"></i>
                                                </a>
                                                <a href="#" className="icon-link performance me-2" title={ t("Performance") } onClick={ handlePerformance } data-id={ a.id }>
                                                    <i className="bi bi-bar-chart-steps"></i>
                                                </a>
                                                <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ a.id }>
                                                    <i className="bi bi-trash"></i>
                                                </a>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            { loaded &&
                <a className="admin-add-new" href="#" onClick={ handleAddNew }>
                    <i className="bi bi-plus-circle"></i>
                </a>
            }
            { showAddNew &&
                <AdminAthleteNew onSave={ handleAddNewOnSave } onClose={ handleAddNewOnClose } />
            }
            { showEdit && (selectedId != 0) &&
                <AdminAthleteEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
            }
            { showAthleteTeamSeason && (selectedId != 0) &&
                <AdminAthleteTeamSeason id={ selectedId } onSave={ handleAthleteTeamSeasonOnSave } onClose={ handleAthleteTeamSeasonOnClose } />
            }
            { showAthleteRanking && (selectedId != 0) &&
                <AdminAthleteRanking id={ selectedId } onSave={ handleAthleteRankingOnSave } onClose={ handleAthleteRankingOnClose } />
            }
            { showDelete && (selectedId != 0) &&
                <AdminAthleteDelete id={ selectedId } onDelete={ handleDeleteOnDelete } onClose={ handleDeleteOnClose } />
            }
            { showPerformance && (selectedId != 0) &&
                <CoachAthletePerformance id={ selectedId } onSave={ handlePerformanceOnSave } onClose={ handlePerformanceOnClose } />
            }
        </div>

    )
}